/**
 * Date normalization utilities for handling timezone-aware dates
 */

/**
 * Normalize a date to local midnight (00:00:00)
 * This ensures consistent day boundaries regardless of timezone
 * @param {Date} date - Date to normalize
 * @returns {Date} - New date set to midnight in local time
 */
export const normalizeToLocalMidnight = (date) => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

/**
 * Add days to a date and normalize to midnight
 * @param {Date} baseDate - Starting date
 * @param {number} days - Number of days to add
 * @returns {Date} - New date with days added, normalized to midnight
 */
export const addDaysNormalized = (baseDate, days) => {
  const normalized = normalizeToLocalMidnight(baseDate);
  normalized.setDate(normalized.getDate() + days);
  return normalized;
};

/**
 * Get the difference in days between two dates (ignoring time)
 * @param {Date} date1 - First date
 * @param {Date} date2 - Second date
 * @returns {number} - Number of days between dates
 */
export const daysDifference = (date1, date2) => {
  const normalized1 = normalizeToLocalMidnight(date1);
  const normalized2 = normalizeToLocalMidnight(date2);
  return Math.round((normalized1 - normalized2) / (1000 * 60 * 60 * 24));
};

/**
 * Format date to YYYY-MM-DD string in LOCAL timezone (not UTC)
 * This prevents timezone offset issues when comparing dates
 * @param {Date} date - Date to format
 * @returns {string} - Date string in YYYY-MM-DD format
 */
export const formatDateToLocalString = (date) => {
  const normalized = normalizeToLocalMidnight(date);
  const year = normalized.getFullYear();
  const month = String(normalized.getMonth() + 1).padStart(2, '0');
  const day = String(normalized.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
